import { supabase } from './supabase';

// Meta Pixel. Off unless VITE_META_PIXEL_ID is set, so local builds don't report to Meta.
const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;

type Fbq = ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void; queue: unknown[] };
declare global {
  interface Window { fbq?: Fbq; _fbq?: Fbq }
}

// Meta's base snippet: a queueing stub until fbevents.js loads (or an ad blocker stops it).
if (PIXEL_ID && !window.fbq) {
  const fbq = ((...args: unknown[]) => {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  }) as Fbq & { push: Fbq; loaded: boolean; version: string };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = window._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  fbq('init', PIXEL_ID);
}

// Pass eventId when the server sends the same event, so Meta counts it once.
export function trackPixel(event: string, params?: Record<string, unknown>, eventId?: string) {
  if (eventId) window.fbq?.('track', event, params ?? {}, { eventID: eventId });
  else window.fbq?.('track', event, params);
}

const cookie = (name: string) => document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))?.[1];

// An ad click lands with ?fbclid=. The Pixel turns it into the _fbc cookie, but not when an ad blocker
// stops it, so write _fbc ourselves (Meta's format, 90 days, shared across uselima.com) for the Conversions API.
const fbclid = new URLSearchParams(window.location.search).get('fbclid');
if (fbclid && !cookie('_fbc')?.endsWith(`.${fbclid}`)) {
  const domain = window.location.hostname.endsWith('uselima.com') ? '; Domain=.uselima.com; Secure' : '';
  document.cookie = `_fbc=fb.1.${Date.now()}.${fbclid}; Max-Age=7776000; Path=/; SameSite=Lax${domain}`;
}

// The Pixel's _fbp/_fbc cookies, sent to edge functions so Conversions API events match this visitor.
export function metaCookies() {
  return { fbp: cookie('_fbp'), fbc: cookie('_fbc') };
}

// CompleteRegistration, once per user: the server claims it and tells the browser the event_id to share.
export async function trackRegistration() {
  const { data } = await supabase.functions.invoke('track-registration', { body: metaCookies() });
  if (data?.eventId) trackPixel('CompleteRegistration', {}, data.eventId);
}

// OnboardingComplete (custom), once per user, for their Trial workspace; claimed the same way.
export async function trackOnboardingComplete(workspaceId: string) {
  const { data } = await supabase.functions.invoke('track-onboarding', { body: { workspaceId, ...metaCookies() } });
  if (data?.eventId) window.fbq?.('trackCustom', 'OnboardingComplete', {}, { eventID: data.eventId });
}
